import { createReducer } from '@reduxjs/toolkit';
import { ClientDto, ClientType, EntityType, IClientDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyCustomer,
    clearClient,
    saveClient,
    importClient,
    createNewClient,
    updateClientAddress,
    updateClientCity,
    updateClientClientType,
    updateClientContactInfo,
    updateClientEmail,
    updateClientEntityType,
    updateClientName,
    updateClientPhone,
    updateClientShortName,
    updateClientTaxId,
    updateClientUcin,
    createNewEmptySupplier,
    updateClientState,
} from '../actions/clientActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface ClientFormReducerState extends IBaseEntityState {
    client: IClientDto | null;
}

function clearState(state: ClientFormReducerState): void {
    state.client = null;

    clearBaseEntityState(state);
}

function getInitialState(): ClientFormReducerState {
    const state: ClientFormReducerState = {
        client: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: ClientFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.client != null, 'Need client to calculate');

    const jsonBaseline = JSON.stringify(state.client);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: ClientFormReducerState) {
    myAssert(() => state.client != null, 'Need client to calculate');

    if (!state.client!.id || state.client!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of client
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.client, undefined);

        state.modified =
            (modified ?? state.client!.created)
                ? { modifiedAt: state.client!.created ?? new Date(), modifiedBy: state.client!.createdBy ?? '' }
                : undefined;
    }
}

export const clientFormReducer = createReducer<ClientFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyCustomer, (state: ClientFormReducerState, action: any) => {
                clearState(state);

                const newClient: IClientDto = new ClientDto();

                newClient.id = 0;
                newClient.address = undefined;
                newClient.city = undefined;
                newClient.clientType = ClientType.Customer;
                newClient.entityType = EntityType.Legal;
                newClient.contactInfo = undefined;
                newClient.email = undefined;
                newClient.name = undefined;
                newClient.phone = undefined;
                newClient.shortName = undefined;
                newClient.taxId = undefined;
                newClient.ucin = undefined;

                state.client = newClient;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewEmptySupplier, (state: ClientFormReducerState, action: any) => {
                clearState(state);

                const newClient: IClientDto = new ClientDto();

                newClient.id = 0;
                newClient.address = undefined;
                newClient.city = undefined;
                newClient.clientType = ClientType.Supplier;
                newClient.entityType = EntityType.Legal;
                newClient.contactInfo = undefined;
                newClient.email = undefined;
                newClient.name = undefined;
                newClient.phone = undefined;
                newClient.shortName = undefined;
                newClient.taxId = undefined;
                newClient.ucin = undefined;

                state.client = newClient;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewClient, (state: ClientFormReducerState, action: any) => {
                clearState(state);

                const payload: IClientDto = action.payload;

                // check client data
                myAssert(() => payload.id === 0, 'It is new client!!!');

                state.client = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importClient.fulfilled, (state: ClientFormReducerState, action: any) => {
                clearState(state);

                const payload: IClientDto = action.payload;

                myAssert(
                    () => !!payload && !!payload.name && !!payload.taxId && payload.name !== '' && payload.taxId !== '',
                    'Required Data for Import',
                );

                state.client = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearClient, (state: ClientFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(updateClientName, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, name: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientShortName, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, shortName: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientAddress, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, address: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientCity, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, city: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientState, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, state: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientClientType, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, clientType: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientContactInfo, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, contactInfo: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientEmail, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, email: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientEntityType, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, entityType: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientPhone, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, phone: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientTaxId, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, taxId: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateClientUcin, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, ucin: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(saveClient.pending, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveClient.fulfilled, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveClient.rejected, (state: ClientFormReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
